import { Injectable } from '@angular/core';
import { Conversation } from '@elevenlabs/client';
import { ElevenLabsVoiceService } from './elevenlabs-voice.service';

@Injectable({ providedIn: 'root' })
export class ElevenLabsAgentService {
    private conversation: any;
    private conversationId = '';

    constructor(private voice: ElevenLabsVoiceService) {}

    async start(
        partnerid: string,
        onMode: (mode: string) => void,
        onError: () => void,
        onMessage: (message: string, source: 'user' | 'ai', conversationId: string) => void,
        onToolResult: (toolName: string, result: unknown) => void,
    ): Promise<string> {
        try {
            await this.stop();
            const signed = await this.voice.createAgentSignedUrl(partnerid);
            if (!signed.signedUrl) throw new Error('Missing ElevenLabs agent signed URL');
            this.conversation = await Conversation.startSession({
                signedUrl: signed.signedUrl,
                onConnect: ({ conversationId }) => {
                    this.conversationId = conversationId;
                    // The native ElevenLabs ID is stable for this live voice
                    // session. Give it to the agent after it exists so every
                    // MCP offer tool can persist and recover the exact same
                    // structured selection on later turns.
                    // `onConnect` can fire before startSession assigns the
                    // returned instance to this.conversation. Defer one task
                    // so the contextual update is never lost on fast connects.
                    setTimeout(() => this.conversation?.sendContextualUpdate?.(
                        `Internal conversation metadata: conversationId=${conversationId}; source=elevenlabs. ` +
                        'For search_travel_offers, remember_presented_hotels, select_presented_offers and send_offerpack_email always pass this exact conversationId and source. Before presenting named hotel ideas without live codes, call remember_presented_hotels. ' +
                        'When the customer refers to shown offers (for example alle drei, alle Angebote, das zweite Hotel or die ersten zwei), call select_presented_offers immediately. Once offers are selected, never replace them with a generic destination search before sending an Offerpack.',
                    ), 0);
                },
                onModeChange: ({ mode }) => onMode(mode),
                onError: () => onError(),
                onMessage: ({ message, source }) => onMessage(message, source, this.conversationId),
                onAgentToolResponse: (event: any) => {
                    // ElevenLabs SDK versions do not consistently use the same
                    // property for an MCP tool response. Do not lose offer cards
                    // merely because the result is wrapped outside full_tool_result.
                    const result = event?.full_tool_result
                        ?? event?.tool_result
                        ?? event?.result
                        ?? event?.response;
                    const toolName = event?.tool_name ?? event?.toolName ?? event?.name;
                    if (toolName && result !== undefined && result !== null) {
                        onToolResult(String(toolName), result);
                    }
                },
                onDisconnect: () => { this.conversation = undefined; this.conversationId = ''; },
            });
            return signed.travelPartnerId;
        } catch (error) {
            console.error('[ElevenLabs agent] Session start failed', error);
            throw error;
        }
    }

    async stop(): Promise<void> {
        if (this.conversation) await this.conversation.endSession();
        this.conversation = undefined;
        this.conversationId = '';
    }
}
